import { useEffect, type ReactNode } from "react";
import { LuX } from "react-icons/lu";
import { motion, AnimatePresence } from "motion/react";
import { IconButton } from "./ui";

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
};

export function Modal({ open, title, onClose, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4"
          style={{ zIndex: 60 }}
          initial={{ backgroundColor: "rgba(15,23,42,0)" }}
          animate={{ backgroundColor: "rgba(15,23,42,0.4)" }}
          exit={{ backgroundColor: "rgba(15,23,42,0)" }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0" onClick={onClose} aria-hidden />
          <motion.div
            className="relative w-full max-w-md overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
            style={{ display: "flex", flexDirection: "column", maxHeight: "80svh" }}
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-base font-semibold text-slate-900">{title}</h3>
              <IconButton icon={<LuX className="h-4 w-4" />} label="Close" onClick={onClose} />
            </div>
            <div style={{ overflowY: "auto", minHeight: 0, padding: "1rem 1.25rem", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
